import { FeatureFlag } from "@asmbl/shared/feature-flags";
import { IconButton } from "@mui/material";
import makeStyles from "../../../makeStylesV4Compat";
import { Link } from "react-router-dom";
import { CompCycleSettingsQuery } from "src/__generated__/graphql";
import { ApprovalIcon } from "src/components/AssembleIcons/Brand/ApprovalIcon";
import { ArrowLeftIcon } from "src/components/AssembleIcons/Brand/ArrowLeftIcon";
import { CompCycleSettingsIcon } from "src/components/AssembleIcons/Brand/CompCycleSettingsIcon";
import { ImportIcon } from "src/components/AssembleIcons/Brand/ImportIcon";
import { PerformanceIcon } from "src/components/AssembleIcons/Brand/PerformanceIcon";
import { RulesIcon } from "src/components/AssembleIcons/Small/RulesIcon";
import { TargetIcon } from "src/components/AssembleIcons/Small/TargetIcon";
import { ThumbsUpIcon } from "src/components/AssembleIcons/Small/ThumbsUpIcon";
import { useFeatureFlags } from "src/components/FeatureContext";
import { AssembleTypography } from "../../../components/AssembleTypography";
import { SidebarButton } from "../../../components/Settings/SettingsSidebar";
import {
  GRAY_1,
  GRAY_4,
  GRAY_5,
  GRAY_6,
  SETTINGS_SIDEBAR_WIDTH,
  SIDEBAR_WIDTH,
  WHITE,
} from "../../../theme";

const useStyles = makeStyles((theme) => ({
  sidebar: {
    height: "100%",
    width: SETTINGS_SIDEBAR_WIDTH,
    position: "fixed",
    overflow: "auto",
    left: SIDEBAR_WIDTH,
    padding: theme.spacing(2, 2),
    zIndex: 10,
    backgroundColor: WHITE,
    borderLeft: `1px solid ${GRAY_6}`,
    borderRight: `1px solid ${GRAY_6}`,
  },
  backButton: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    border: `1px solid ${GRAY_5}`,
    boxSizing: "border-box",
    boxShadow: "0px 1px 3px rgba(10, 36, 64, 0.1)",
    borderRadius: "5px",
    marginRight: theme.spacing(2),
    padding: "0px",

    // Tweak position so it lines up with the comp cycle header on previous page
    marginTop: "3.5px",
    marginLeft: "-1px",
  },
  compCycleText: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

export function CompCycleSettingsSidebar({
  compCycle,
}: {
  compCycle: CompCycleSettingsQuery["compCycle"];
}): JSX.Element {
  const classes = useStyles();
  const { isEnabled } = useFeatureFlags();

  if (!compCycle) {
    return <></>;
  }

  const compCycleId = compCycle.id;
  const shouldShowBonusGuidance =
    compCycle.allowSalaryMerit || compCycle.allowActualRecurringBonus;

  return (
    <div className={classes.sidebar}>
      <IconButton
        component={Link}
        to={`/comp-cycles/${compCycleId}`}
        className={classes.backButton}
        size="large"
      >
        <ArrowLeftIcon
          height="18px"
          width="18px"
          color={GRAY_4}
          hoverColor={GRAY_1}
        />
      </IconButton>
      <div className={classes.compCycleText}>
        <AssembleTypography variant="productEyebrow" textColor={GRAY_4}>
          Cycle Settings
        </AssembleTypography>
      </div>
      <SidebarButton
        icon={<CompCycleSettingsIcon width="16px" height="16px" inherit />}
        path={`/comp-cycles/${compCycleId}/settings/general`}
      >
        General Settings
      </SidebarButton>
      <SidebarButton
        icon={<PerformanceIcon width="16px" height="16px" inherit />}
        path={`/comp-cycles/${compCycleId}/settings/performance`}
      >
        Performance Ratings
      </SidebarButton>
      <SidebarButton
        icon={<RulesIcon width="16px" height="16px" inherit />}
        path={`/comp-cycles/${compCycleId}/settings/eligibility`}
      >
        Eligibility
      </SidebarButton>
      {shouldShowBonusGuidance && (
        <SidebarButton
          icon={<TargetIcon width="16px" height="16px" inherit />}
          activePaths={[
            `/comp-cycles/${compCycleId}/settings/guidance-and-budget/merit`,
            `/comp-cycles/${compCycleId}/settings/guidance-and-budget/bonus`,
            `/comp-cycles/${compCycleId}/settings/multiple-merit-matrices`,
          ]}
          path={
            isEnabled(FeatureFlag.BonusGuidance)
              ? `/comp-cycles/${compCycleId}/settings/guidance-and-budget/merit`
              : `/comp-cycles/${compCycleId}/settings/multiple-merit-matrices`
          }
        >
          {isEnabled(FeatureFlag.BonusGuidance)
            ? "Guidance & Budget"
            : "Merit & Budget"}
        </SidebarButton>
      )}
      <SidebarButton
        icon={<ImportIcon width="16px" height="16px" inherit />}
        path={`/comp-cycles/${compCycleId}/settings/requests`}
      >
        Upload Requests
      </SidebarButton>
      {compCycle.phases.length > 0 && (
        <SidebarButton
          icon={<ThumbsUpIcon width="16px" height="16px" inherit />}
          path={`/comp-cycles/${compCycleId}/settings/approvals`}
        >
          Approvals & Phases
        </SidebarButton>
      )}
      <SidebarButton
        icon={<ApprovalIcon width="16px" height="16px" inherit />}
        path={`/comp-cycles/${compCycleId}/settings/close`}
      >
        Close Cycle
      </SidebarButton>
    </div>
  );
}
